import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import moment from 'moment';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import {
  gerarRelatorioContasSemSaldoConferido,
  exportarRelatorioContasSemSaldoConferidoExcel,
  listarGruposEmpresaHierarquia,
  listarGruposGELookup,
} from '~/Services/Service';
import CardFiltros, { TIPOS_CONTA_OPTIONS } from './components/CardFiltros';
import CardResultado from './components/CardResultado';

const TAMANHO_PAGINA = 20;

// W-0928 — Defaults paridade ERP (PRD §4.1 + URelContasSemSaldoConf.dfm)
const DEFAULT_VALUES = {
  tipoAtivo: 'Moedas',
  tiposConta: TIPOS_CONTA_OPTIONS.map((o) => o.value),
  codigoGE: null,
  codigoGrupoGE: null,
  diasMinimos: 30,
  qtdeMovtosMinima: 1,
  codigoContaEspecifica: null,
  naoConsiderarSaldoConfRecente: false,
  agrupamento: 'GrupoEmpresaConta',
  quebraPagina: false,
};

// W-0928 — Yup schema completo (LICOES §1.6 + TechSpec §3.4)
const filtroSchema = Yup.object()
  .shape({
    tipoAtivo: Yup.string()
      .oneOf(['Moedas', 'MoedasPrazo', 'Imoveis', 'Imobilizado'])
      .required('Tipo de ativo é obrigatório'),
    tiposConta: Yup.array()
      .of(
        Yup.number()
          .integer()
          .min(1)
          .max(8)
      )
      .min(1, 'Selecione ao menos um tipo de conta')
      .required('Selecione ao menos um tipo de conta'),
    codigoGE: Yup.number()
      .integer()
      .nullable(),
    codigoGrupoGE: Yup.number()
      .integer()
      .nullable(),
    diasMinimos: Yup.number()
      .typeError('Dias deve ser número')
      .integer('Dias deve ser inteiro')
      .min(0, 'Dias deve ser >= 0')
      .required('Dias é obrigatório'),
    qtdeMovtosMinima: Yup.number()
      .typeError('Quantidade deve ser número')
      .integer('Quantidade deve ser inteira')
      .min(0, 'Quantidade deve ser >= 0')
      .required('Quantidade é obrigatória'),
    codigoContaEspecifica: Yup.number()
      .integer()
      .nullable(),
    naoConsiderarSaldoConfRecente: Yup.boolean(),
    agrupamento: Yup.string().oneOf([
      'UsuarioResponsavel',
      'GrupoEmpresaConta',
      'Vendedor',
    ]),
    quebraPagina: Yup.boolean(),
  })
  .test(
    'ge-ou-grupo-ge',
    'GE e Grupo de GE são mutuamente exclusivos',
    (values) => !(values.codigoGE && values.codigoGrupoGE)
  );

const ORDENACAO_DEFAULT_POR_AGRUPAMENTO = {
  UsuarioResponsavel: { coluna: 'nome_usuario', direcao: 'asc' },
  GrupoEmpresaConta: { coluna: 'codigo_grupo_empresa', direcao: 'asc' },
  Vendedor: { coluna: 'vendedor', direcao: 'asc' },
};

const construirPayload = (values, pagina, ordenacao) => ({
  tipo_ativo: values.tipoAtivo,
  tipos_conta: values.tiposConta,
  codigo_ge: values.codigoGE,
  codigo_grupo_ge: values.codigoGrupoGE,
  dias_minimos: values.diasMinimos,
  qtde_movtos_minima: values.qtdeMovtosMinima,
  codigo_conta_especifica: values.codigoContaEspecifica,
  nao_considerar_saldo_conf_recente: !!values.naoConsiderarSaldoConfRecente,
  agrupamento: values.agrupamento,
  quebra_pagina: !!values.quebraPagina,
  pagina,
  tamanho_pagina: TAMANHO_PAGINA,
  ordenar_por: ordenacao.coluna,
  direcao_ordenacao: ordenacao.direcao,
});

const ContasSemSaldoConferido = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [gruposGE, setGruposGE] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(true);

  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const [ultimoFiltro, setUltimoFiltro] = useState(null);
  const [ordenacao, setOrdenacao] = useState(
    ORDENACAO_DEFAULT_POR_AGRUPAMENTO.GrupoEmpresaConta
  );

  // proteção contra resposta stale (LICOES Ciclo 9 L3) — incrementa a cada fetch
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const [ges, grupos] = await Promise.all([
          listarGruposEmpresaHierarquia(),
          listarGruposGELookup(),
        ]);
        if (!ativo) return;
        setGruposEmpresa(Array.isArray(ges) ? ges : ges.itens || []);
        setGruposGE(
          Array.isArray(grupos) ? grupos : grupos.itens || []
        );
      } catch (err) {
        if (!ativo) return;
        const msg =
          err && err.message
            ? err.message
            : 'Erro ao carregar opções de filtro.';
        toast.error(msg);
      } finally {
        if (ativo) setLoadingOpcoes(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  const executarBusca = async (values, pagina, ordenacaoAtual) => {
    // Validação UX literal — paridade ERP (PRD §5.3 + Q-07)
    if (!values.tiposConta || values.tiposConta.length === 0) {
      toast.warning('Nenhum tipo de conta foi selecionado');
      return;
    }

    const payload = construirPayload(values, pagina, ordenacaoAtual);
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;

    setLoading(true);
    try {
      const data = await gerarRelatorioContasSemSaldoConferido(payload);
      // Descarta resposta stale (LICOES Ciclo 9 L3)
      if (seq !== fetchSeqRef.current) return;
      setResultado(data);
      setUltimoFiltro({ values, ordenacao: ordenacaoAtual });
      setPaginaAtual(pagina);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      const msg =
        err && err.message
          ? err.message
          : 'Erro ao gerar relatório.';
      toast.error(msg);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const novaOrdenacao =
      ORDENACAO_DEFAULT_POR_AGRUPAMENTO[values.agrupamento] ||
      ORDENACAO_DEFAULT_POR_AGRUPAMENTO.GrupoEmpresaConta;
    setOrdenacao(novaOrdenacao);
    executarBusca(values, 1, novaOrdenacao).finally(() =>
      setSubmitting(false)
    );
  };

  const handleMudarPagina = (novaPagina) => {
    if (!ultimoFiltro) return;
    if (novaPagina < 1) return;
    if (
      resultado &&
      resultado.total_paginas &&
      novaPagina > resultado.total_paginas
    )
      return;
    executarBusca(ultimoFiltro.values, novaPagina, ordenacao);
  };

  const handleMudarOrdenacao = (novaOrdenacao) => {
    if (!ultimoFiltro) return;
    setOrdenacao(novaOrdenacao);
    executarBusca(ultimoFiltro.values, 1, novaOrdenacao);
  };

  const handleExportar = async () => {
    if (!ultimoFiltro) {
      toast.warning('Gere o relatório antes de exportar.');
      return;
    }
    setExportando(true);
    try {
      const payload = construirPayload(
        ultimoFiltro.values,
        1,
        ordenacao
      );
      const blob = await exportarRelatorioContasSemSaldoConferidoExcel(
        payload
      );
      const nomeArquivo = `contas-sem-saldo-conferido-${moment().format(
        'YYYY-MM-DD'
      )}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg =
        err && err.message
          ? err.message
          : 'Erro ao exportar relatório.';
      toast.error(msg);
    } finally {
      setExportando(false);
    }
  };

  const agrupamentoAtual = useMemo(
    () =>
      ultimoFiltro
        ? ultimoFiltro.values.agrupamento
        : DEFAULT_VALUES.agrupamento,
    [ultimoFiltro]
  );

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Contas sem saldo conferido" />

      <Formik
        initialValues={DEFAULT_VALUES}
        validationSchema={filtroSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
          submitForm,
          resetForm,
        }) => (
          <Form>
            <CardFiltros
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              isSubmitting={isSubmitting}
              submitForm={submitForm}
              resetForm={resetForm}
              defaultValues={DEFAULT_VALUES}
              gruposEmpresa={gruposEmpresa}
              gruposGE={gruposGE}
              loading={loading}
              loadingOpcoes={loadingOpcoes}
            />

            <CardResultado
              resultado={resultado}
              loading={loading}
              exportando={exportando}
              agrupamento={agrupamentoAtual}
              paginaAtual={paginaAtual}
              tamanhoPagina={TAMANHO_PAGINA}
              ordenacao={ordenacao}
              onMudarPagina={handleMudarPagina}
              onMudarOrdenacao={handleMudarOrdenacao}
              onExportar={handleExportar}
            />
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default ContasSemSaldoConferido;
